import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchRecommendedUsers, 
  selectRecommendedUsers, 
  selectUserInfo, 
  getApiBaseUrl 
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { RecommendedUsersLogicReturn } from './RecommendedUsers.types';

// 전역 중복 방지 맵
const globalFetchMap = new Map<string, boolean>();

/**
 * 날짜 형식화 함수
 */
const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

export const useRecommendedUsersLogic = (componentProps: any = {}): RecommendedUsersLogicReturn => {
  const { t } = useTranslation();
  const dispatch = useDispatch() as any;
  
  // Redux store에서 사용자 정보 및 추천 사용자 데이터 가져오기
  const userInfo = useSelector(selectUserInfo);
  const apiBaseUrl = useSelector(getApiBaseUrl);
  const { items, loading, error, pagination } = useSelector(selectRecommendedUsers);
  
  // 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const limit = componentProps.itemsPerPage || 10;
  
  // API 호출 중복 방지 ref
  const fetchInProgressRef = useRef(false);
  const lastFetchParamsRef = useRef<string>('');
  const renderCountRef = useRef(0);
  const mountedRef = useRef(true);
  
  // 컴포넌트 언마운트 감지
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      fetchInProgressRef.current = false;
    };
  }, []);
  
  // 렌더링 횟수 추적
  renderCountRef.current += 1;
  console.log(`[RecommendedUsers] 렌더링 #${renderCountRef.current}`, {
    userInfo: !!userInfo,
    currentPage,
    limit,
    loading,
    items: items?.length
  });
  
  // 페이지 변경 핸들러
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);
  
  // userInfo의 ID만 추출하여 의존성 안정화
  const userId = userInfo?.id;
  
  // 추천 사용자 데이터 가져오기
  useEffect(() => {
    console.log(`[RecommendedUsers] useEffect 실행`, {
      userId,
      currentPage,
      limit,
      fetchInProgress: fetchInProgressRef.current,
      lastParams: lastFetchParamsRef.current
    });
    
    if (!userId) {
      console.log(`[RecommendedUsers] userId 없음 - API 호출 스킵`);
      return;
    }
    
    // 현재 호출 파라미터 생성
    const currentParams = JSON.stringify({ page: currentPage, limit, userId });
    const globalKey = `recommended-users-${currentParams}`;
    
    // 전역 중복 방지 체크
    if (globalFetchMap.get(globalKey)) {
      console.log(`[RecommendedUsers] 전역 중복 방지 - 스킵`, globalKey);
      return;
    }
    
    // 로컬 중복 방지 체크
    if (fetchInProgressRef.current) {
      console.log(`[RecommendedUsers] 로컬 호출 중 - 스킵`);
      return;
    }
    
    if (lastFetchParamsRef.current === currentParams) {
      console.log(`[RecommendedUsers] 같은 파라미터로 이미 호출함 - 스킵`, currentParams);
      return;
    }
    
    // API 호출 시작 표시
    globalFetchMap.set(globalKey, true);
    fetchInProgressRef.current = true;
    lastFetchParamsRef.current = currentParams;
    
    console.log(`[RecommendedUsers] API 호출 시작`, currentParams);
    
    dispatch(fetchRecommendedUsers({ page: currentPage, limit }))
      .finally(() => {
        // 호출 완료 후 플래그 정리
        if (mountedRef.current) {
          fetchInProgressRef.current = false;
        }
        globalFetchMap.delete(globalKey);
        console.log(`[RecommendedUsers] API 호출 완료`, currentParams);
      });
  }, [dispatch, userId, currentPage, limit]);
  
  // 페이지네이션 버튼 생성
  const generatePaginationButtons = useCallback(() => {
    if (!pagination.totalPages || pagination.totalPages <= 1) {
      return [];
    }
    
    const buttons = [];
    const totalPages = pagination.totalPages;
    const current = currentPage;
    
    // 이전 버튼
    if (current > 1) {
      buttons.push({
        type: 'prev',
        page: current - 1,
        label: '이전',
        disabled: false
      });
    }
    
    // 페이지 번호 버튼들
    let startPage = Math.max(1, current - 2);
    let endPage = Math.min(totalPages, current + 2);
    
    // 시작/끝 조정
    if (endPage - startPage < 4) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + 4);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, endPage - 4);
      }
    }
    
    // 첫 페이지
    if (startPage > 1) {
      buttons.push({
        type: 'page',
        page: 1,
        label: '1',
        active: current === 1,
        disabled: false
      });
      
      if (startPage > 2) {
        buttons.push({
          type: 'ellipsis',
          page: -1,
          label: '...',
          disabled: true
        });
      }
    }
    
    // 중간 페이지들
    for (let i = startPage; i <= endPage; i++) {
      buttons.push({
        type: 'page',
        page: i,
        label: i.toString(),
        active: current === i,
        disabled: false
      });
    }
    
    // 마지막 페이지
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push({
          type: 'ellipsis',
          page: -1,
          label: '...',
          disabled: true
        });
      }
      
      buttons.push({
        type: 'page',
        page: totalPages,
        label: totalPages.toString(),
        active: current === totalPages,
        disabled: false
      });
    }
    
    // 다음 버튼
    if (current < totalPages) {
      buttons.push({
        type: 'next',
        page: current + 1,
        label: '다음',
        disabled: false
      });
    }
    
    return buttons;
  }, [pagination.totalPages, currentPage]);
  
  // 필터링된 아이템들
  const filteredItems = useMemo(() => {
    return items || [];
  }, [items]);
  
  // 검색 기능
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredBySearch, setFilteredBySearch] = useState(filteredItems);
  
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredBySearch(filteredItems);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = filteredItems.filter(user => 
      user.name?.toLowerCase().includes(query) ||
      user.insungId?.toLowerCase().includes(query)
    );
    setFilteredBySearch(filtered);
  }, [searchQuery, filteredItems]);
  
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
  }, []);
  
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setCurrentPage(1);
  }, []);
  
  // 새로고침 기능
  const handleRefresh = useCallback(() => {
    if (userId) {
      setCurrentPage(1);
      // 캐시 무효화
      fetchInProgressRef.current = false;
      lastFetchParamsRef.current = '';
      
      dispatch(fetchRecommendedUsers({ page: 1, limit }));
    }
  }, [dispatch, userId, limit]);
  
  return {
    // Redux 상태
    userInfo,
    apiBaseUrl,
    items: filteredBySearch,
    allItems: filteredItems,
    loading,
    error,
    pagination,
    
    // 로컬 상태
    currentPage,
    limit,
    searchQuery,
    
    // 액션
    handlePageChange,
    handleSearch,
    clearSearch,
    handleRefresh,
    
    // 유틸리티
    formatDate,
    generatePaginationButtons,
    t,
    
    // 렌더링 정보
    renderCount: renderCountRef.current
  };
};